import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { ExpandMore, FilePresent } from '@vedmarket/ui-kit';

import {COLORS} from "../../../../../../../config/constants";
import {numberWithSpaces} from "../../../../../../../utils/helpers";
import {formatTerm} from './tariff-terms';

import './services.scss';

export {formatTerm, formatTerms} from './tariff-terms';

const DetailsSection = ({isActive, title, children, expandAll, isSearchResult}: DeliveryDetailsSectionProps) => {
  const [ expanded, setExpanded ] = useState(false);

  useEffect(() => {
    if (expandAll !== undefined) {
      setExpanded(expandAll);
    }
  }, [ expandAll ]);

  return (
    <Accordion
      className='delivery-details-section'
      expanded={expandAll === undefined ? undefined : expanded}
      onChange={(_, value) => setExpanded(value)}
      disableGutters
      square
      sx={{
        background: isActive ? COLORS.LIME : COLORS.WHITE,
        boxShadow: 'none',
        margin: 0,
        '&.Mui-expanded': {
          margin: 0,
        },
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 56,
          padding: 0,
          '&.Mui-expanded': {
            borderBottom: 0,
            minHeight: 56,
          },
          '& .MuiAccordionSummary-content': {
            margin: '16px 0',
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: '16px 0',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: COLORS.LIGHT_ACTION,
            position: 'relative',
            top: 2,
          },
          '& .MuiAccordionSummary-expandIconWrapper svg': {
            height: 24,
            width: 24,
          },
        }}
      >
        <Typography variant='subtitle1' sx={{
          color: COLORS.BLACK_DARK,
          lineHeight: '24px',
          fontSize: isSearchResult ? '16px' : undefined,
          fontWeight: isSearchResult ? 500 : undefined,
          letterSpacing: isSearchResult ? 0 : undefined,
        }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{padding: '0 0 24px'}}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const getDocumentTitle = (document: FreightDocument) => {
  if (document.title.toLowerCase().endsWith('.pdf')) {
    return document.title;
  }

  return `${document.title}.pdf`;
}

const Services = ({isActive, services, terms, documents, isSearchResult, expandAll, details}: DeliveryServicesProps) => {
  const { t } = useTranslation()

  const hasTariffDescription = Boolean(details?.tariffDescription);
  const shouldShowTariffTerms = terms.length > 0 && !hasTariffDescription;
  const isSelectedSearchResult = isSearchResult && isActive;
  const useSelectedServicesLabel = isSelectedSearchResult && !hasTariffDescription;
  const useSelectedConditionsLabel = isSelectedSearchResult && hasTariffDescription;
  const servicesLabel = useSelectedServicesLabel
    ? 'calc__text__delivery__tariffServices'
    : 'calc__text__delivery__servicesIncludedInTariff';
  const pricedServicesLabel = useSelectedConditionsLabel
    ? 'calc__text__delivery__tariffConditions'
    : servicesLabel;

  const descriptionSection = hasTariffDescription && <>
    <DetailsSection
      isActive={isActive}
      isSearchResult={isSearchResult}
      expandAll={expandAll}
      title={t('calc__text__delivery__tariffServices')}
    >
      <Typography variant='body2' sx={{
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '21px',
        letterSpacing: 0,
        color: COLORS.BLACK_DARK,
      }}>
        {details?.tariffDescription}
      </Typography>
    </DetailsSection>
    <Divider sx={{margin: 0}} />
  </>;

  const termsSection = <>
    {shouldShowTariffTerms && <DetailsSection
      isActive={isActive}
      isSearchResult={isSearchResult}
      expandAll={expandAll}
      title={t(isSearchResult ? 'calc__text__delivery__tariffConditions' : 'calc__text__delivery__tariffServices')}
    >
      <div className='delivery-services__tariff-freights'>
        {terms.map((freight) => (
          <div key={freight.freightId}>
            <Typography variant='body2' sx={{color: COLORS.BLACK_DARK, lineHeight: 1.5}}>
              {freight.locationFrom} - {freight.locationTo}:
            </Typography>
            <ul className='delivery-services__tariff-terms'>
              {freight.terms.map((term, index) => (
                <Typography
                  component='li'
                  className={isSearchResult ? 'delivery-services__tariff-term' : undefined}
                  key={index}
                  variant='body2'
                  sx={{color: COLORS.BLACK_DARK, lineHeight: 1.5}}
                >
                  {formatTerm(term)}
                </Typography>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </DetailsSection>}
    {shouldShowTariffTerms && <Divider sx={{margin: 0}}/>}
  </>;

  const servicesSection = <>
    {services.length > 0 && <DetailsSection
      isActive={isActive}
      isSearchResult={isSearchResult}
      expandAll={expandAll}
      title={t(pricedServicesLabel)}
    >
      <div className='delivery-services__services-list'>
        {services.map((service, index) => (
          <div className='delivery-services__service-row' key={`${service.id || service.name}-${index}`}>
            <Typography
              className='delivery-services__service-name'
              variant='body2'
              sx={{color: COLORS.LIGHT_TEXT_SECONDARY, lineHeight: 1.5}}
            >
              {service.name}
            </Typography>
            <Typography
              className='delivery-services__service-price'
              variant='body2'
              sx={{color: COLORS.BLACK_DARK, lineHeight: 1.5}}
            >
              {numberWithSpaces(service.value)} {service.currency}
            </Typography>
          </div>
        ))}
      </div>
    </DetailsSection>}
    {services.length > 0 && <Divider sx={{margin: 0}}/>}
  </>;

  const documentsSection = <>
    {documents.length > 0 && <DetailsSection
      isActive={isActive}
      isSearchResult={isSearchResult}
      expandAll={expandAll}
      title={t('calc__text__delivery__documents')}
    >
      <div className='delivery-services__documents-list'>
        {documents.map((document, index) => (
          <div className='delivery-services__document' key={`${document.link}-${index}`}>
            <FilePresent className='delivery-services__document-icon'/>
            <Link
              href={document.link}
              underline={isSearchResult ? 'hover' : 'always'}
              target='_blank'
              rel='noreferrer'
              sx={{
                color: COLORS.BLUE,
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1.5,
                letterSpacing: isSearchResult ? 0 : undefined,
                '&:hover': {
                  color: COLORS.BLACK_DARK,
                },
              }}
            >
              <Typography component='span' variant='inherit'>
                {getDocumentTitle(document)}
              </Typography>
              {details?.documentSizes?.[document.link] && <>
                {' '}
                <Typography component='span' variant='inherit' sx={{color: COLORS.LIGHT_TEXT_DISABLED}}>
                  ({details.documentSizes[document.link]})
                </Typography>
              </>}
            </Link>
          </div>
        ))}
      </div>
    </DetailsSection>}
    {documents.length > 0 && <Divider sx={{margin: 0}}/>}
  </>;

  return <div className={isSearchResult ? 'delivery-services delivery-services_search-result' : 'delivery-services'}>
    {descriptionSection}
    {isSearchResult ? <>{servicesSection}{termsSection}</> : <>{termsSection}{servicesSection}</>}
    {documentsSection}
  </div>;

}

export default Services
